import {
  ButtonDS,
  ModalDS,
  Spacer,
  TextCapitalized,
  WarningOutlined,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectAdminUser } from "../../homeSlice";
import { deleteCompanyAsync, updateCompanyStatusAsync } from "../followUpSlice";
import { CompanyFollowUpDisplayed } from "../followUpApi";
import "../../../i18n/i18n";

type UpdateCompanyStatusModalProps = {
  visible: boolean;
  onClose: () => void;
  company: {
    uuid: string;
    name: string;
    status: CompanyFollowUpDisplayed["status"];
  } | null;
};

export const UpdateCompanyStatusModal = ({
  visible,
  onClose,
  company,
}: UpdateCompanyStatusModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const adminUser = useAppSelector(selectAdminUser);

  const clickToSubmit = useCallback(() => {
    if (!company || !adminUser) return;

    if (company.status === "DELETE") {
      void dispatch(
        deleteCompanyAsync({
          companyUuid: company.uuid,
          deleteBy: adminUser.email,
        }),
      );
    } else {
      void dispatch(
        updateCompanyStatusAsync({
          companyUuid: company.uuid,
          status: company.status,
          updatedBy: adminUser.email,
        }),
      );
    }

    onClose();
  }, [adminUser, company, dispatch, onClose]);

  if (!company) return;

  return (
    <ModalDS visible={visible} onClose={onClose} width={300}>
      <StyledModalContainer>
        <StyledIcon>
          <WarningOutlined />
        </StyledIcon>
        <Spacer y={2} />
        <StyledModalTitle>
          <TextCapitalized>
            {t(
              `companyFollowUp.updateCompanyStatus.modal.title.${company.status}`,
              {
                name: company.name,
              },
            )}
          </TextCapitalized>
        </StyledModalTitle>
        <Spacer y={1} />
        <StyledModalText>
          <TextCapitalized>
            {t(
              `companyFollowUp.updateCompanyStatus.modal.alert.${company.status}`,
              {
                name: company.name,
              },
            )}
          </TextCapitalized>
        </StyledModalText>
        <Spacer y={2} />
        <StyledButtonContainer>
          <ButtonDS
            text={t(`companyFollowUp.updateCompanyStatus.modal.cancelButton`)}
            format="fill"
            buttonType="secondary"
            onClick={onClose}
          />
          <Spacer x={1} />
          <ButtonDS
            text={t(
              `companyFollowUp.updateCompanyStatus.modal.confirmButton.${company.status}`,
            )}
            format="fill"
            buttonType={company.status === "ACTIVE" ? "primary" : "danger"}
            onClick={clickToSubmit}
          />
          <Spacer y={2} />
        </StyledButtonContainer>
      </StyledModalContainer>
    </ModalDS>
  );
};

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const StyledModalTitle = styled.div`
  ${typographies["Header/H3"]};
  text-align: center;
  color: ${colors["colors/text/black"]};
`;

const StyledModalText = styled.div`
  ${typographies["Body/M"]};
  text-align: center;
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledIcon = styled.div`
  & svg {
    width: 3.5em;
    height: 3.5em;
  }
  height: 4rem;
  width: 4rem;
  border-radius: 0.5rem;
  fill: ${colors["colors/system/error/error_normal"]};
  justify-content: center;
  display: flex;
  align-items: center;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  width: 100%;
`;
